'use client';

import { ReactNode, useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { getThemeBackgroundClass } from '@/lib/theme';
import { ITheme } from '@/types/theme';
import { IBreadcrumbItem } from '@/interfaces';
import { Language } from '@/lib/translations';
import TitleBarSub from './TitleBarSub';
import WaveDivider from './WaveDivider';

interface HeroSectionProps {
  title: string;
  subtitle: string;
  searchPlaceholder?: string;
  onSearch?: (value: string) => void;
  badge?: ReactNode;
  theme?: ITheme;
  language: Language;
  // Explicit breadcrumb items; when omitted they are derived from the pathname
  breadcrumbItems?: IBreadcrumbItem[];
}

export default function HeroSection({
  title,
  subtitle,
  searchPlaceholder,
  onSearch,
  badge,
  theme: themeColor = null,
  language,
  breadcrumbItems,
}: HeroSectionProps) {
  const { theme } = useTheme();
  const [searchValue, setSearchValue] = useState('');

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    onSearch?.(value);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    onSearch?.('');
  };

  const gradientClass = getThemeBackgroundClass(themeColor, 'secondary');

  return (
    <section
      className={`relative overflow-hidden bg-gradient-to-br ${gradientClass}`}
    >
      {/* Breadcrumbs - positioned at absolute top */}
      <div className="absolute top-0 left-0 right-0 z-10 pt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TitleBarSub language={language} breadcrumbItems={breadcrumbItems} />
        </div>
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <h1
            className="text-4xl lg:text-5xl font-bold mb-6"
            style={{ color: 'var(--color-foreground)' }}
          >
            {title}
          </h1>
          <p
            className="text-xl mb-12 max-w-3xl mx-auto"
            style={{ color: 'var(--color-foreground-secondary)' }}
          >
            {subtitle}
          </p>

          {/* Search Bar */}
          {searchPlaceholder && onSearch && (
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400 dark:text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchValue}
                  className={`block w-full pl-10 ${searchValue.length > 0 ? 'pr-10' : 'pr-3'} py-3 border rounded-lg leading-5 focus:outline-none focus:ring-1 transition-colors ${
                    theme === 'light'
                      ? 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500'
                      : theme === 'dark'
                        ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:placeholder-gray-500 focus:ring-blue-400 focus:border-blue-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 dark:focus:placeholder-gray-500 dark:focus:ring-blue-400 dark:focus:border-blue-400'
                  }`}
                  placeholder={searchPlaceholder}
                  onChange={e => handleSearchChange(e.target.value)}
                />
                {/* Clear button */}
                {searchValue.length > 0 && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors cursor-pointer"
                      aria-label="Clear search"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Badge */}
          {badge && <div className="mt-8">{badge}</div>}
        </div>
      </div>
      <WaveDivider
        position="bottom"
        backgroundColor="secondary"
        theme={themeColor}
      />
    </section>
  );
}
